import type { FC } from 'react';
import { memo } from 'react';

import { BaseStepCountStep } from './components/BaseStepCountStep'
import { BaseStepCountStepsList } from './components/BaseStepCountStepsList'
import { BaseStepCountStyled } from './styles/BaseStepCountStyled'
import {
  type BaseStepCountComponents,
  type StepCountCommonProps,
  type StepStatus,
} from './types'

export const StepCountComponents: BaseStepCountComponents = {
  StepList: BaseStepCountStepsList,
  Step: BaseStepCountStep,
}

const getStepStatus = (
  currentStep: number,
  stepIndex: number,
  showTrailingSteps: boolean
): StepStatus => {
  if (
    showTrailingSteps ? currentStep >= stepIndex : currentStep === stepIndex
  ) {
    return 'active'
  } else if (currentStep >= stepIndex) {
    return 'complete'
  } else {
    return 'incomplete'
  }
}

export interface IBaseStepCountProps {
  step?: number
  total?: number
  clickable?: boolean
  trailingSteps?: boolean
  components?: Partial<BaseStepCountComponents>
  onStepClick?: (clickedStep: number) => void
}

export const BaseStepCount: FC<IBaseStepCountProps> = memo(
  ({
    step = 1,
    total = 1,
    clickable = false,
    trailingSteps = false,
    components,
    onStepClick = () => undefined,
  }) => {
    const ExtendedComponents: BaseStepCountComponents = {
      ...StepCountComponents,
      ...components,
    }

    const commonProps: StepCountCommonProps = {
      stepCountProps: {
        step,
        total,
        clickable,
        trailingSteps,
        components: ExtendedComponents,
      },
      getStepStatus,
      innerProps: {},
    }

    return (
      <BaseStepCountStyled>
        <div className='step-list'>
          <ExtendedComponents.StepList {...commonProps}>
            {new Array(total).fill(0).map((_, index) => (
              <ExtendedComponents.Step
                key={index}
                {...commonProps}
                innerProps={{
                  stepIndex: index,
                  stepStatus: getStepStatus(step, index + 1, trailingSteps),
                  onClick: clickable ? () => onStepClick(index + 1) : null,
                }}
              />
            ))}
          </ExtendedComponents.StepList>
        </div>
      </BaseStepCountStyled>
    )
  }
)

BaseStepCount.displayName = 'BaseStepCount'
